import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { API_COMPAT_VERSION, API_COMPAT_VERSION_HEADER } from '@repo/utils/apiCompat';

import { reportOutdatedClient } from '../utils/common/outdatedClient';
import { holdReload, ReloadHoldKind } from '../utils/common/reloadHolds';
import { getAccessToken } from './auth';

const FILE_TRANSFER_MAX_RETRIES = 2;

// Retry only transient failures; deterministic errors (eg. 404/403) never heal
const retryTransientErrorsOnly = {
  retryCondition: (error: unknown, _args: unknown, { attempt }: { attempt: number }): boolean => {
    const status = (error as FetchBaseQueryError | undefined)?.status;
    return (
      attempt <= FILE_TRANSFER_MAX_RETRIES &&
      (status === 'FETCH_ERROR' ||
        status === 'TIMEOUT_ERROR' ||
        (typeof status === 'number' && status >= 500))
    );
  },
};

const UPGRADE_REQUIRED = 426;

const backendBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_URI,
  prepareHeaders: async headers => {
    // Attach the token when we have one; when signed out (or refresh
    // failed) omit the header entirely rather than sending
    // "Bearer undefined"
    const accessToken = await getAccessToken();
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    headers.set(API_COMPAT_VERSION_HEADER, String(API_COMPAT_VERSION));
    return headers;
  },
});

/**
 * Runs a base query, holding any reload onto a new build while it is in flight.
 * Reads too, since a run of writes on the page being left can wait on one, such
 * as an upload URL.
 */
const withRequestsHoldingReload =
  <Args, Result, QueryError, ExtraOptions, Meta>(
    baseQuery: BaseQueryFn<Args, Result, QueryError, ExtraOptions, Meta>
  ): BaseQueryFn<Args, Result, QueryError, ExtraOptions, Meta> =>
  async (args, api, extraOptions) => {
    const release = holdReload(api.type === 'query' ? ReloadHoldKind.Read : ReloadHoldKind.Work);
    try {
      return await baseQuery(args, api, extraOptions);
    } finally {
      release();
    }
  };

// The API answers 426 to a build too old for it, having done nothing with the request
const backendBaseQueryReportingOutdated: typeof backendBaseQuery = async (
  args,
  api,
  extraOptions
) => {
  const result = await backendBaseQuery(args, api, extraOptions);
  if (result.error?.status === UPGRADE_REQUIRED) {
    reportOutdatedClient({ isWrite: api.type === 'mutation' });
  }
  return result;
};

// Cognito API for authenticated requests to the backend
export const cognitoApi = createApi({
  reducerPath: 'cognitoApi',
  baseQuery: withRequestsHoldingReload(backendBaseQueryReportingOutdated),
  tagTypes: [
    'Users',
    'Tutorials',
    'Students',
    'Staff',
    'StudentActivityLog',
    'Videos',
    'VideoCategories',
    'VideoPermissions',
    'Leads',
    'Permissions',
    'PermissionGroups',
    'Prices',
    'Notes',
    'LegacyExamMarkingRequests',
    'NotesCategories',
    'NotesPermissions',
    'LegacyExams',
    'LegacyExamTypes',
    'LegacyExamQuestions',
    'LegacyExamAttempts',
    'LegacyExamAttemptsByMarker',
    'Rooms',
    'Schools',
    'Classes',
    'UserNotes',
    'LessonEnrolments',
    'LegacyTopics',
    'LegacyTopicGroups',
    'StudentAnalytics',
    'Courses',
    'Streams',
    'Lessons',
    'LessonTemplates',
    'QuestionSets',
    'QuestionSetCandidates',
    'QuestionEnrichment',
    'QuestionTree',
    'QuestionContent',
    'QuestionFaults',
    'QuestionDigitisationRuns',
    'SourceDocuments',
    'AttemptScans',
    'AttemptSubmissions',
    'Attempts',
  ],
  endpoints: () => ({}),
});

// General API for external services like S3, CloudFront, etc.
// This API doesn't require authentication headers like the Cognito API
export const generalApi = createApi({
  reducerPath: 'generalApi',
  // Endpoints opt into retries via extraOptions; everything else is single-shot
  baseQuery: withRequestsHoldingReload(
    retry(
      fetchBaseQuery({
        baseUrl: '', // No base URL since we'll use full URLs
      }),
      { maxRetries: 0 }
    )
  ),
  tagTypes: [],
  endpoints: builder => ({
    // GET a PDF file from an external URL
    getPdf: builder.query<string | undefined, { url: string }>({
      query: ({ url }) => ({
        url,
        method: 'GET',
        responseHandler: async (response: Response) => {
          // Don't fabricate a PDF blob from an error/HTML body (e.g. an S3 404).
          // fetchBaseQuery still flags the query as errored when !response.ok.
          if (!response.ok) {
            return undefined;
          }
          const blob = new Blob([await response.arrayBuffer()], { type: 'application/pdf' });
          // Convert blob to object URL immediately and return the URL
          return URL.createObjectURL(blob);
        },
      }),
      extraOptions: retryTransientErrorsOnly,
    }),

    // Upload a PDF file to an external URL
    putPdf: builder.mutation<void, { url: string; pdf: Uint8Array | File }>({
      query: ({ url, pdf }) => ({
        url,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/pdf',
        },
        body: pdf,
      }),
      // A paper's worth of annotations is at stake, so ride out network blips
      extraOptions: retryTransientErrorsOnly,
    }),

    // Upload a file to an external URL as the type named, which a browser cannot always tell
    putFile: builder.mutation<void, { url: string; file: Blob; contentType: string }>({
      query: ({ url, file, contentType }) => ({
        url,
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
        },
        body: file,
      }),
      extraOptions: retryTransientErrorsOnly,
    }),

    // Upload an image file to an external URL
    putImage: builder.mutation<void, { url: string; image: File }>({
      query: ({ url, image }) => ({
        url,
        method: 'PUT',
        headers: {
          'Content-Type': image.type,
        },
        body: image,
      }),
    }),
  }),
});

export const {
  useGetPdfQuery,
  useLazyGetPdfQuery,
  usePutFileMutation,
  usePutPdfMutation,
  usePutImageMutation,
} = generalApi;
